import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCw, RotateCcw, Crop, Check, Send, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { rotateImage, cropImage, createPreviewUrl, revokePreviewUrl, blobToFile } from '../../utils/imageEditor';

const ImagePreviewModal = ({ isOpen, onClose, imageFile, onSend }) => {
  const { t } = useTranslation('chat');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [currentImageFile, setCurrentImageFile] = useState(null);

  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [cropArea, setCropArea] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const captionRef = useRef(null);

  // Init on open
  useEffect(() => {
    if (imageFile && isOpen) {
      setCurrentImageFile(imageFile);
      setCaption('');
      setCropMode(false);
      setCropArea(null);
      createPreviewUrl(imageFile).then(setPreviewUrl).catch(console.error);
    }
    return () => {
      if (previewUrl) revokePreviewUrl(previewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile, isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (previewUrl) revokePreviewUrl(previewUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentImageFile, caption, isSending]);

  const handleRotate = async (dir) => {
    if (!currentImageFile || isProcessing) return;
    setIsProcessing(true);
    try {
      // currentImageFile zaten son halinin pixel verisi — her seferinde 0'dan 90° döndür
      const blob = await rotateImage(currentImageFile, 0, dir);
      const file = blobToFile(blob, currentImageFile.name);
      if (previewUrl) revokePreviewUrl(previewUrl);
      const url = await createPreviewUrl(file);
      setPreviewUrl(url);
      setCurrentImageFile(file);
      setCropArea(null);
    } catch (e) {
      console.error('Rotation error:', e);
      dispatchToast('error', t('imagePreviewModal.rotateErrorTitle'), t('imagePreviewModal.rotateErrorMessage'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Crop drag handlers
  const handleMouseDown = useCallback((e) => {
    if (!cropMode || !imageRef.current) return;
    e.preventDefault();
    const imgRect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - imgRect.left;
    const y = e.clientY - imgRect.top;
    setDragStart({ x, y });
    setCropArea({ x, y, width: 0, height: 0 });
    setIsDragging(true);
  }, [cropMode]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !cropMode || !imageRef.current) return;
    const imgRect = imageRef.current.getBoundingClientRect();
    const curX = Math.max(0, Math.min(e.clientX - imgRect.left, imgRect.width));
    const curY = Math.max(0, Math.min(e.clientY - imgRect.top, imgRect.height));
    const x = Math.min(dragStart.x, curX);
    const y = Math.min(dragStart.y, curY);
    const width = Math.abs(curX - dragStart.x);
    const height = Math.abs(curY - dragStart.y);
    setCropArea({ x, y, width, height });
  }, [isDragging, cropMode, dragStart]);

  const handleMouseUp = useCallback(() => { setIsDragging(false); }, []);

  const handleApplyCrop = async () => {
    if (!currentImageFile || !cropArea || cropArea.width < 10 || cropArea.height < 10 || isProcessing) return;
    if (!imageRef.current) return;
    setIsProcessing(true);
    try {
      // Displayed image boyutu ile gerçek pixel boyutu arasındaki ölçek farkı
      const displayW = imageRef.current.offsetWidth;
      const displayH = imageRef.current.offsetHeight;
      const naturalW = imageRef.current.naturalWidth;
      const naturalH = imageRef.current.naturalHeight;
      const scaleX = naturalW / displayW;
      const scaleY = naturalH / displayH;

      const scaledArea = {
        x: Math.round(cropArea.x * scaleX),
        y: Math.round(cropArea.y * scaleY),
        width: Math.round(cropArea.width * scaleX),
        height: Math.round(cropArea.height * scaleY),
      };

      const blob = await cropImage(currentImageFile, scaledArea, 0);
      const file = blobToFile(blob, currentImageFile.name);
      if (previewUrl) revokePreviewUrl(previewUrl);
      const url = await createPreviewUrl(file);
      setPreviewUrl(url);
      setCurrentImageFile(file);
      setCropMode(false);
      setCropArea(null);
    } catch (e) {
      console.error('Crop error:', e);
      dispatchToast('error', t('imagePreviewModal.cropErrorTitle'), t('imagePreviewModal.cropErrorMessage'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSend = async () => {
    if (!currentImageFile || isSending) return;
    setIsSending(true);
    try {
      await onSend(currentImageFile, caption.trim());
      if (previewUrl) revokePreviewUrl(previewUrl);
      setPreviewUrl(null);
      setCurrentImageFile(null);
      setCaption('');
      setCropArea(null);
      setCropMode(false);
      onClose();
    } catch (e) {
      console.error('Send error:', e);
      dispatchToast('error', t('imagePreviewModal.sendErrorTitle'), t('imagePreviewModal.sendErrorMessage'));
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
    setCurrentImageFile(null);
    setCaption('');
    setCropArea(null);
    setCropMode(false);
    onClose();
  };

  const dispatchToast = (type, title, message) => {
    window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type, title, message } }));
  };

  const hasCrop = cropArea && cropArea.width > 10 && cropArea.height > 10;
  const fileName = currentImageFile?.name || '';

  if (!isOpen || !imageFile) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[100000] flex flex-col"
          style={{ background: 'rgba(5,9,18,0.97)', backdropFilter: 'blur(8px)' }}
        >
          {/* ─── Top bar ─── */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
            {/* Left: close */}
            <motion.button
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={handleClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </motion.button>

            {/* Center: filename */}
            <p className="text-[13px] text-white/60 font-medium truncate max-w-[200px] select-none">{fileName}</p>

            {/* Right: edit tools */}
            <div className="flex items-center gap-1">
              {/* Rotate left */}
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => handleRotate(-1)}
                disabled={isProcessing}
                title={t('imagePreviewModal.rotateLeft')}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-40"
              >
                <RotateCcw className="w-4 h-4" />
              </motion.button>

              {/* Rotate right */}
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => handleRotate(1)}
                disabled={isProcessing}
                title={t('imagePreviewModal.rotateRight')}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-40"
              >
                <RotateCw className="w-4 h-4" />
              </motion.button>

              {/* Crop toggle */}
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => { setCropMode((p) => !p); setCropArea(null); }}
                disabled={isProcessing}
                title={t('imagePreviewModal.crop')}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
                  cropMode
                    ? 'bg-[#2563eb] text-white'
                    : 'hover:bg-white/10 text-white/70 hover:text-white'
                }`}
              >
                <Crop className="w-4 h-4" />
              </motion.button>

              {/* Apply crop */}
              <AnimatePresence>
                {cropMode && hasCrop && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={handleApplyCrop}
                    disabled={isProcessing}
                    title={t('imagePreviewModal.applyCrop')}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-500 hover:bg-emerald-400 text-white transition-colors disabled:opacity-40"
                  >
                    <Check className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Processing spinner */}
              {isProcessing && (
                <div className="w-9 h-9 flex items-center justify-center">
                  <Loader className="w-4 h-4 text-white/50 animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* ─── Image area ─── */}
          <div
            ref={containerRef}
            className="flex-1 flex items-center justify-center px-4 pb-2 overflow-hidden relative select-none"
            style={{ cursor: cropMode ? 'crosshair' : 'default' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {previewUrl && (
              <div className="relative max-w-full max-h-full flex items-center justify-center">
                <img
                  ref={imageRef}
                  src={previewUrl}
                  alt={t('imagePreviewModal.previewAlt')}
                  draggable={false}
                  className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                  style={{ maxHeight: 'calc(100vh - 200px)' }}
                />

                {/* Crop overlay */}
                {cropMode && cropArea && cropArea.width > 2 && cropArea.height > 2 && imageRef.current && (
                  <>
                    {/* Dimmed overlay with crop hole */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `rgba(0,0,0,0.55)`,
                        maskImage: `
                          linear-gradient(black, black),
                          rect(
                            ${cropArea.y}px,
                            ${cropArea.x + cropArea.width}px,
                            ${cropArea.y + cropArea.height}px,
                            ${cropArea.x}px
                          )
                        `,
                      }}
                    />
                    {/* Crop border */}
                    <div
                      className="absolute border-2 border-white pointer-events-none"
                      style={{
                        left: `${cropArea.x}px`,
                        top: `${cropArea.y}px`,
                        width: `${cropArea.width}px`,
                        height: `${cropArea.height}px`,
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                      }}
                    >
                      {/* Corner handles */}
                      {[
                        { top: -3, left: -3 },
                        { top: -3, right: -3 },
                        { bottom: -3, left: -3 },
                        { bottom: -3, right: -3 },
                      ].map((style, i) => (
                        <div
                          key={i}
                          className="absolute w-3 h-3 border-2 border-white bg-white/20"
                          style={style}
                        />
                      ))}
                      {/* Rule of thirds grid */}
                      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
                        {Array.from({ length: 9 }).map((_, i) => (
                          <div key={i} className="border border-white/20" />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Crop hint */}
            <AnimatePresence>
              {cropMode && !hasCrop && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white/80 text-xs backdrop-blur-sm pointer-events-none"
                >
                  {t('imagePreviewModal.cropHint')}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ─── Bottom bar ─── */}
          <div className="flex-shrink-0 px-4 pb-5 pt-2">
            <div
              className="flex items-end gap-3 rounded-2xl px-4 py-3"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {/* Caption */}
              <textarea
                ref={captionRef}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={t('imagePreviewModal.captionPlaceholder')}
                rows={1}
                className="flex-1 bg-transparent text-white placeholder-white/35 text-[14px] resize-none outline-none leading-relaxed py-0.5"
                style={{ minHeight: '24px', maxHeight: '96px' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
              />

              {/* Send button */}
              <motion.button
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.92 }}
                onClick={handleSend}
                disabled={isSending || isProcessing}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-[#2563eb] hover:bg-[#3d8ef0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                style={{ boxShadow: '0 4px 16px rgba(61,142,240,0.35)' }}
              >
                {isSending ? (
                  <Loader className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" style={{ transform: 'rotate(-10deg)' }} />
                )}
              </motion.button>
            </div>

            {/* Shortcut hint */}
            <p className="text-center text-[11px] text-white/25 mt-2 select-none">
              {t('imagePreviewModal.shortcutHint')}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImagePreviewModal;
